import { Box, Flex, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';

import { SectionHeader } from '../SectionHeader/SectionHeader';
import { TestimonialCard, type TestimonialVariant } from '../TestimonialCard/TestimonialCard';

interface Msg {
  id: string;
  defaultMessage: string;
}

interface Testimonial {
  id: string;
  variant?: TestimonialVariant;
  quote: Msg;
}

// Order matches the design grid as it flattens on mobile: the dark (accent) card first,
// then the column nearest it (سارة / حسين), then the far column (ليلى / رغد).
const TESTIMONIALS: Testimonial[] = [
  {
    id: 'mohammed',
    variant: 'dark',
    quote: {
      id: 'about.testimonials.mohammed.quote',
      defaultMessage:
        'From the moment I joined Ticket Zone, attending events became far simpler. I booked a Kadhim Al-Sahir ticket in two minutes and it arrived instantly on my phone. The best platform we have in Iraq.',
    },
  },
  {
    id: 'sara',
    quote: {
      id: 'about.testimonials.sara.quote',
      defaultMessage:
        'A smooth experience from the first search to entering the event. The app is clean and fast.',
    },
  },
  {
    id: 'hussein',
    variant: 'accent',
    quote: {
      id: 'about.testimonials.hussein.quote',
      defaultMessage: 'We buy all our tickets from Ticket Zone — it has become a family habit.',
    },
  },
  {
    id: 'layla',
    quote: {
      id: 'about.testimonials.layla.quote',
      defaultMessage:
        'Customer service answered me in under a minute — something I have not seen anywhere else.',
    },
  },
  {
    id: 'raghad',
    quote: {
      id: 'about.testimonials.raghad.quote',
      defaultMessage:
        'Payment is fast and secure, and I never had any problem receiving the ticket by email.',
    },
  },
];

/**
 * About page Testimonials section (TZ-389): the "ماذا يقولون عنا" header over a grid of
 * {@link TestimonialCard}s. The dark (accent) card spans the full height on the start
 * (right) side, with the remaining reviews stacked two-per-column beside it. On mobile
 * the grid flattens to a single full-width column (cards size to their text). RTL.
 */
export const TestimonialsSection = () => {
  const accentItem = TESTIMONIALS.find((t) => t.variant === 'dark');
  const restItems = TESTIMONIALS.filter((t) => t.variant !== 'dark');
  const columns: Testimonial[][] = [];
  for (let i = 0; i < restItems.length; i += 2) columns.push(restItems.slice(i, i + 2));

  // Two stacked standard cards (2·250 + 20 gap) equal the accent card's height on md.
  const tallH = { base: 'auto', md: '520px' };
  const shortH = { base: 'auto', md: '250px' };

  const renderCard = (item: Testimonial) => (
    <TestimonialCard
      variant={item.variant}
      quote={<FormattedMessage id={item.quote.id} defaultMessage={item.quote.defaultMessage} />}
    />
  );

  return (
    <Box as='section' dir='rtl' bg='white' aria-labelledby='about-testimonials-heading'>
      <VStack
        w='100%'
        maxW='container.xl'
        mx='auto'
        px={{ base: '20px', md: '100px' }}
        py={{ base: '48px', md: '80px' }}
        spacing={{ base: '24px', md: '40px' }}
        align='stretch'
      >
        <SectionHeader
          titleId='about-testimonials-heading'
          eyebrow={
            <FormattedMessage id='about.testimonials.eyebrow' defaultMessage='User reviews' />
          }
          eyebrowProps={{ fontWeight: '700', fontSize: { base: '15px', md: '16px' } }}
          title={
            <FormattedMessage
              id='about.testimonials.title'
              defaultMessage='What they say about us'
            />
          }
          titleProps={{ fontWeight: '900', fontSize: { base: '30px', md: '44px' } }}
        />

        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: '16px', md: '20px' }}
          align='stretch'
        >
          {accentItem && (
            <Box flex={{ md: '1.25' }} h={tallH}>
              {renderCard(accentItem)}
            </Box>
          )}
          {columns.map((col, ci) => (
            <Flex key={ci} direction='column' flex={{ md: '1' }} gap={{ base: '16px', md: '20px' }}>
              {col.map((item) => (
                <Box key={item.id} h={shortH}>
                  {renderCard(item)}
                </Box>
              ))}
            </Flex>
          ))}
        </Flex>
      </VStack>
    </Box>
  );
};
